import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import '../pages/Contact.css';
import { ContactQueryApi } from '../api/adminApi';
import Toast from '../components/Toast';

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 }
};

const sectionTransition = (delay = 0) => ({
  duration: 0.5,
  ease: 'easeOut',
  delay
});

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [toast, setToast] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    // basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setToast({ type: 'error', message: 'Please fill the required fields (Name, Email, Message).' });
      return;
    }
    setStatus({ state: 'loading', message: 'Sending your message…' });
    ContactQueryApi.submit({
      name: formData.name.trim(),
      subject: formData.subject.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      source: 'contact-page'
    })
      .then(() => {
        setStatus({ state: 'success', message: "Thanks! We'll reach out shortly." });
        setFormData({ name: '', subject: '', email: '', message: '' });
        setToast({ type: 'success', message: "Thanks! We'll reach out shortly." });
        setTimeout(() => {
          setStatus({ state: 'idle', message: '' });
        }, 2000);
      })
      .catch((error) => {
        const message = error.response?.data?.message || 'Unable to send your message right now.';
        setStatus({ state: 'error', message });
        setToast({ type: 'error', message });
      });
  }

  return (
    <>
      <motion.main
        className="contact-page"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={sectionTransition(0)}
      >
        <div className="contact-inner">
          <motion.div
            className="contact-heading-group"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={sectionTransition(0.05)}
          >
            <span className="contact-label">CONTACT</span>
            <h1 className="contact-title">Contact us for any questions !</h1>
          </motion.div>

          <motion.div
            className="contact-grid"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={sectionTransition(0.1)}
          >
            <motion.div
              className="contact-info"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              transition={sectionTransition(0.15)}
            >
              <p className="info-heading">Contact Info :</p>
              <ul className="info-list" aria-label="Company contact information">
                <li>
                  <span className="info-icon" aria-hidden="true">{/* Mail Icon */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </span>
                  <span className="info-text">tanya.hill@example.com</span>
                </li>
                <li>
                  <span className="info-icon" aria-hidden="true">{/* Phone Icon */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.05 20 19.5 19.5 0 0 1 3.11 12.95 19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 11a16 16 0 0 0 6 6l2.36-1.17a2 2 0 0 1 2.11.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </span>
                  <span className="info-text">(480) 555-0103</span>
                </li>
                <li>
                  <span className="info-icon" aria-hidden="true">{/* Location Icon */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </span>
                  <span className="info-text">2464 Royal Ln. Mesa, New Jersey 45463</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              className="contact-form-wrapper"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              transition={sectionTransition(0.2)}
            >
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row two-cols">
                  <div className="form-field">
                    <label htmlFor="name" className="visually-hidden">Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="subject" className="visually-hidden">Subject</label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="email" className="visually-hidden">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email ID"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="message" className="visually-hidden">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder="Message"
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-btn" disabled={status.state === 'loading'}>
                    {status.state === 'loading' ? 'Submitting…' : 'Submit'}
                  </button>
                  {status.state === 'loading' && (
                    <span className="submit-feedback" role="status" aria-live="polite">
                      Sending your message…
                    </span>
                  )}
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </motion.main>
      <Toast
        message={toast?.message}
        type={toast?.type}
        onDismiss={() => setToast(null)}
      />
    </>
  );
}

export default ContactPage;
